import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete.tsx';
import type { AppInventoryOptionType } from '@openx/types/targeting/appInventory.ts';
import type { InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes.ts';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { comparisonOptions } from '../../../InventoryAndContent/InventoryAndContentTopBox/constants.ts';
import { ifComparisonOptionDoesntExist } from '../../../InventoryAndContent/InventoryAndContentTopBox/utils/ifComparisonOptionDoesntExist.ts';

type InventoryComparisonTypeDropdownProps = {
  onChange: (action: ComparisonType) => void;
  value: ComparisonType | null;
  option: InventoryContentOption | AppInventoryOptionType;
  hasInventoryList?: boolean;
  customComparisonOptions?: Record<string, ComparisonType[]>;
};

export const InventoryComparisonTypeDropdown = ({
  onChange,
  value,
  option,
  hasInventoryList = false,
  customComparisonOptions,
}: InventoryComparisonTypeDropdownProps) => {
  const { t } = useTranslation();

  const labels = {
    [ComparisonType.EQUALS]: t('Equals'),
    [ComparisonType.DOES_NOT_EQUAL]: t('Does not equal'),
    [ComparisonType.ENDS_WITH]: t('Ends with'),
    [ComparisonType.CONTAINS]: t('Contains'),
    [ComparisonType.BEGINS_WITH]: t('Begins with'),
    [ComparisonType.DOES_NOT_END_WITH]: t('Does not end with'),
    [ComparisonType.DOES_NOT_CONTAIN]: t('Does not contain'),
    [ComparisonType.DOES_NOT_BEGIN_WITH]: t('Does not begin with'),
  };

  useEffect(() => {
    ifComparisonOptionDoesntExist(value, option) && onChange(comparisonOptions[option][0]);
  }, [value, onChange, option]);

  return (
    <AutoComplete
      options={customComparisonOptions ? customComparisonOptions[option] : comparisonOptions[option]}
      renderOptions={{
        dataTest: 'comparison-option',
      }}
      getOptionLabel={(option: ComparisonType) => labels[option]}
      textFieldReadOnly
      disableClearable
      onChange={(_e, value) => onChange(value)}
      value={ifComparisonOptionDoesntExist(value, option) ? comparisonOptions[option][0] : value}
      data-test="inventory-comparison-options"
      getOptionDisabled={option => {
        if (hasInventoryList) {
          return option === ComparisonType.DOES_NOT_BEGIN_WITH || option === ComparisonType.DOES_NOT_EQUAL;
        }

        return false;
      }}
      textFieldProps={{
        label: t('Comparison Type'),
      }}
    />
  );
};
